import { useCallback, useState } from 'react'
import type { OpportunityListItem } from './useOpportunities'

export interface OpportunityDialogInfo {
  id: number
  customerName?: string | null
  salesforceProjectName?: string | null
  title: string
}

export interface CopyDialogInfo {
  id: number
  salesforce_project_id: number | null
  title: string
  information_source?: string | null
  description?: string | null
  is_expansion?: boolean
  start_on?: string | null
  contact_name?: string | null
}

export interface OpportunityStatusDialogInfo {
  id: number
  title: string
  g_company_name?: string | null
  g_company_contact?: string | null
}

/**
 * 各種ダイアログの状態を管理するカスタムフック
 *
 * 複数のダイアログの開閉状態と選択されたオポチュニティ情報を
 * 一元管理することで、index.tsx の見通しを改善します。
 */
export function useOpportunityDialogs() {
  // ダイアログの開閉状態
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [commentDialogOpen, setCommentDialogOpen] = useState(false)
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [closeReasonDialogOpen, setCloseReasonDialogOpen] = useState(false)
  const [gCompanyDialogOpen, setGCompanyDialogOpen] = useState(false)

  // 選択されたオポチュニティの状態
  const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunityDialogInfo | null>(null)
  const [selectedCopyOpportunity, setSelectedCopyOpportunity] = useState<CopyDialogInfo | null>(null)
  const [selectedStatusOpportunity, setSelectedStatusOpportunity] = useState<OpportunityStatusDialogInfo | null>(null)
  const [pendingStatusId, setPendingStatusId] = useState<number | null>(null)

  // コメントダイアログを開く
  const handleCommentClick = useCallback((opportunity: OpportunityListItem) => {
    setSelectedOpportunity({
      id: opportunity.id,
      customerName: opportunity.customer_name,
      salesforceProjectName: opportunity.salesforce_project_name,
      title: opportunity.title,
    })
    setCommentDialogOpen(true)
  }, [])

  // タグ編集ダイアログを開く
  const handleTagEditClick = useCallback((opportunity: OpportunityListItem) => {
    setSelectedOpportunity({
      id: opportunity.id,
      title: opportunity.title,
    })
    setTagDialogOpen(true)
  }, [])

  // コピーダイアログを開く
  const handleCopyClick = useCallback((opportunity: OpportunityListItem) => {
    setSelectedCopyOpportunity({
      id: opportunity.id,
      salesforce_project_id: opportunity.salesforce_project_id,
      title: opportunity.title,
      information_source: opportunity.information_source,
      description: opportunity.description,
      is_expansion: opportunity.is_expansion,
      start_on: opportunity.start_on,
      contact_name: opportunity.contact_name,
    })
    setCopyDialogOpen(true)
  }, [])

  return {
    // ダイアログの開閉状態
    searchDialogOpen,
    setSearchDialogOpen,
    commentDialogOpen,
    setCommentDialogOpen,
    tagDialogOpen,
    setTagDialogOpen,
    copyDialogOpen,
    setCopyDialogOpen,
    closeReasonDialogOpen,
    setCloseReasonDialogOpen,
    gCompanyDialogOpen,
    setGCompanyDialogOpen,

    // 選択されたオポチュニティ
    selectedOpportunity,
    setSelectedOpportunity,
    selectedCopyOpportunity,
    setSelectedCopyOpportunity,
    selectedStatusOpportunity,
    setSelectedStatusOpportunity,
    pendingStatusId,
    setPendingStatusId,

    // ハンドラー
    handleCommentClick,
    handleTagEditClick,
    handleCopyClick,
  }
}
